import { useEffect, useMemo, useRef, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import "./TopVulnerabilityCharts.css";
// @ts-ignore
import { api } from "../../../api/client.tsx";
// @ts-ignore
import { Loader } from "../../../Loading/Loading.tsx";

interface TopVulnerabilityChartsProps {
  selectedImage: string;
}

type Row = { name: string; count: number };

const ORDER = ["Critical", "High", "Medium", "Low", "Negligible", "Unknown"] as const;

const COLORS: Record<(typeof ORDER)[number], string> = {
  Critical: "#e74c3c",
  High: "#f39c12",
  Medium: "#f1c40f",
  Low: "#2ecc71",
  Negligible: "#60a5fa",
  Unknown: "#9CA3AF",
};

function Help({ id, tip, label }: { id: string; tip: string; label: string }) {
  return (
    <button
      type="button"
      className="tvc-help"
      aria-label={`What is "${label}"?`}
      aria-describedby={id}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      ?
      <span id={id} className="tvc-help-tip" role="tooltip">
        {tip}
      </span>
    </button>
  );
}

function CardTitle({
  children,
  id,
  tip,
}: {
  children: React.ReactNode;
  id: string;
  tip: string;
}) {
  return (
    <div className="tvc-card-labelRow">
      <div className="tvc-card-label">{children}</div>
      <Help id={id} tip={tip} label={String(children)} />
    </div>
  );
}

const RADIAN = Math.PI / 180;

const renderSeverityLabel = (props: any) => {
  const { cx, cy, midAngle, outerRadius, percent, name } = props;

  const radius = outerRadius + 26;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const yBase = cy + radius * Math.sin(-midAngle * RADIAN);

  const offsets: Record<string, number> = {
    Medium: -22,
    High: -6,
    Critical: -6,
    Unknown: 6,
    Negligible: 18,
    Low: -4,
  };

  const y = yBase + (offsets[name as string] ?? 0);

  const color = COLORS[name as keyof typeof COLORS] ?? "#374151";
  const textAnchor = x > cx ? "start" : "end";

  return (
    <text
      x={x}
      y={y}
      fill={color}
      textAnchor={textAnchor}
      dominantBaseline="central"
      style={{ fontWeight: 600, fontSize: 13 }}
    >
      {`${name}: ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const TopVulnerabilityCharts: React.FC<TopVulnerabilityChartsProps> = ({
  selectedImage,
}) => {
  const [totalCVEs, setTotalCVEs] = useState<number | null>(null);
  const [uniquePackages, setUniquePackages] = useState<number | null>(null);
  const [totalLayers, setTotalLayers] = useState<number | null>(null);

  const [severityMap, setSeverityMap] = useState<Record<string, number> | null>(
    null
  );

  const pieData = useMemo(
    () =>
      severityMap
        ? ORDER.map((name) => ({ name, value: Number(severityMap[name] ?? 0) }))
        : null,
    [severityMap]
  );

  const [activeSeverity, setActiveSeverity] = useState<string | null>(null);

  const [tvOpen, setTvOpen] = useState(false);
  const [tvSeverity, setTvSeverity] = useState<string>("");
  const [tvRows, setTvRows] = useState<Row[]>([]);
  const [tvLoading, setTvLoading] = useState(false);
  const popRef = useRef<HTMLDivElement | null>(null);

  // --------- LOAD DATA (now using cached Level2 bundle) ---------
  useEffect(() => {
    if (!selectedImage) return;

    (async () => {
      try {
        // 1) Try cached Level 2 bundle
        const bundle = await api.stats.level2Bundle(selectedImage);
        setTotalCVEs(Number(bundle?.total_vulnerabilities ?? 0));
        setUniquePackages(
          Number(bundle?.unique_vulnerable_packages ?? 0)
        );
        setTotalLayers(Number(bundle?.total_layers ?? 0));
        setSeverityMap(bundle?.severity_map || {});
      } catch (err) {
        console.error(
          "[TopVulnerabilityCharts] level2Bundle failed, falling back to individual endpoints",
          err
        );
        // 2) Fallback to the four original endpoints (non-cached)
        try {
          const [cve, unique, layers, severity] = await Promise.all([
            api.stats.totalVulnerabilities(selectedImage),
            api.stats.uniquePackages(selectedImage),
            api.stats.totalLayers(selectedImage),
            api.stats.severityByImage(selectedImage),
          ]);
          setTotalCVEs(Number(cve?.total_vulnerabilities ?? 0));
          setUniquePackages(
            Number(unique?.unique_vulnerable_packages ?? 0)
          );
          setTotalLayers(Number(layers?.total_layers ?? 0));
          setSeverityMap(severity || {});
        } catch (err2) {
          console.error(
            "[TopVulnerabilityCharts] fallback endpoints failed",
            err2
          );
          setTotalCVEs(null);
          setUniquePackages(null);
          setTotalLayers(null);
          setSeverityMap({});
        }
      }
    })();
  }, [selectedImage]);

  const emitFilter = (sev: string | null) => {
    window.dispatchEvent(
      new CustomEvent("level2:severityFilter", { detail: sev })
    );
  };

  useEffect(() => {
    setActiveSeverity(null);
    emitFilter(null);
  }, [selectedImage]);

  const openTv = async (sev: string) => {
    setTvOpen(true);
    setTvSeverity(sev);
    setTvLoading(true);
    setTvRows([]);
    try {
      const rows: Row[] = await api.stats.topLevel2BySeverity(
        selectedImage,
        sev
      );
      setTvRows(rows || []);
    } catch {
      setTvRows([]);
    } finally {
      setTvLoading(false);
    }
  };

  const onSliceClick = (sev: string) => {
    const next = activeSeverity === sev ? null : sev;
    setActiveSeverity(next);
    emitFilter(next);
    if (next) openTv(next);
    else setTvOpen(false);
  };

  const clearFilter = () => {
    setActiveSeverity(null);
    emitFilter(null);
    setTvOpen(false);
  };

  useEffect(() => {
    if (!tvOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setTvOpen(false);
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!popRef.current?.contains(t)) setTvOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("click", onDoc, true);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onDoc, true);
    };
  }, [tvOpen]);

  return (
    <div className="tvc-container">
      <div className="tvc-cards">
        <div className="tvc-card">
          <div className="tvc-card-value">
            {totalCVEs === null ? <Loader /> : totalCVEs.toLocaleString()}
          </div>
          <CardTitle
            id="tvc-tip-total"
            tip="Total vulnerabilities found in this image across all packages and layers."
          >
            Total CVEs
          </CardTitle>
        </div>

        <div className="tvc-card">
          <div className="tvc-card-value">
            {uniquePackages === null ? (
              <Loader />
            ) : (
              uniquePackages.toLocaleString()
            )}
          </div>
          <CardTitle
            id="tvc-tip-unique"
            tip="Number of distinct packages in this image that have at least one CVE."
          >
            Unique Vulnerable Packages
          </CardTitle>
        </div>

        <div className="tvc-card">
          <div className="tvc-card-value">
            {totalLayers === null ? <Loader /> : totalLayers.toLocaleString()}
          </div>
          <CardTitle
            id="tvc-tip-layers"
            tip="Total image layers that contain at least one vulnerable package."
          >
            Total Layers Affected
          </CardTitle>
        </div>
      </div>

      <div className="tvc-chart">
        {activeSeverity && (
          <button className="tvc-clear" onClick={clearFilter} title="Clear filter">
            Clear filter
          </button>
        )}
      </div>

      <div className="tvc-chart-box">
        <div className="tvc-chart-head">
          <div className="tvc-titleRow">
            <h3>Severity Breakdown</h3>
            <Help
              id="tvc-tip-severity"
              label="Severity Breakdown"
              tip="Distribution of CVEs by severity for this image. Click a slice to filter by that severity and see the top affected packages. Use Clear to reset."
            />
          </div>
          {!pieData ? (
            <div className="tvc-empty">
              <Loader />
            </div>
          ) : pieData.every((s) => s.value === 0) ? (
            <div className="tvc-empty">No CVEs in this image.</div>
          ) : (
            <ResponsiveContainer width="90%" height={520}>
              <PieChart>
                <Tooltip
                  formatter={(v: number, _n: string, it: any) => {
                    const name = it?.payload?.name as string;
                    return [`${Number(v).toLocaleString()} CVEs`, name];
                  }}
                />
                <Pie
                  data={pieData}
                  nameKey="name"
                  dataKey="value"
                  cx="50%"
                  cy="47%"
                  innerRadius={82}
                  outerRadius={130}
                  paddingAngle={3}
                  isAnimationActive={false}
                  labelLine={true}
                  label={renderSeverityLabel}
                  onClick={(_, idx) => {
                    const sev = pieData[idx]?.name;
                    if (sev) onSliceClick(sev);
                  }}
                >
                  {pieData.map((slice) => {
                    const sev = slice.name as (typeof ORDER)[number];
                    const dim = activeSeverity && activeSeverity !== sev;
                    return (
                      <Cell
                        key={sev}
                        fill={COLORS[sev]}
                        style={{ cursor: "pointer", opacity: dim ? 0.35 : 1 }}
                      />
                    );
                  })}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {tvOpen && (
        <>
          <div className="tvc-backdrop" />
          <div
            className={`tv-popover tv-popover--${tvSeverity.toLowerCase()}`}
            ref={popRef}
            role="dialog"
            aria-modal="true"
          >
            <div className="tv-accent-bar" />
            <div className="tv-popover-header">
              <span>
                Top 5 <b>{tvSeverity}</b> packages
              </span>
              <button
                className="tv-popover-close"
                onClick={() => setTvOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {tvLoading ? (
              <div className="tv-popover-empty">Loading…</div>
            ) : tvRows.length === 0 ? (
              <div className="tv-popover-empty">No packages found.</div>
            ) : (
              <ul className="tv-popover-list">
                {tvRows.map((r) => (
                  <li key={r.name} className="tv-popover-item">
                    <span className="name" title={r.name}>
                      {r.name}
                    </span>
                    <span className="count">{r.count}</span>
                  </li>
                ))}
              </ul>
            )}
            <div className="tv-popover-hint">Click outside to close</div>
          </div>
        </>
      )}
    </div>
  );
};

export default TopVulnerabilityCharts;
